import Map from 'ol/Map';
import { Coordinate } from 'ol/coordinate';
import { containsCoordinate } from 'ol/extent';

const DEFAULT_TARGET_ZOOM = 11;
const DEFAULT_DURATION = 500;

export interface SmartZoomOptions {
  /** Target coordinate in the map's display projection */
  coordinate: Coordinate;
  /** OpenLayers map instance */
  map: Map;
  /** Preferred zoom level when zooming in from far out (default: 11) */
  targetZoom?: number;
  /** Use smooth animation (default: true) */
  animate?: boolean;
  /** Animation duration in ms (default: 500) */
  duration?: number;
}

/**
 * Smart zoom: pans/zooms to a coordinate following industry best practices.
 *
 * Rules:
 * 1. Never zoom OUT below the user's current zoom level.
 * 2. If the user is zoomed out far (below targetZoom), zoom IN to targetZoom.
 * 3. If the target is already visible at a good zoom, just pan.
 */
export const smartZoomTo = ({
  coordinate,
  map,
  targetZoom = DEFAULT_TARGET_ZOOM,
  animate = true,
  duration = DEFAULT_DURATION,
}: SmartZoomOptions): void => {
  // Guard against invalid coordinates (NaN, Infinity)
  if (!Number.isFinite(coordinate[0]) || !Number.isFinite(coordinate[1])) {
    return;
  }

  const view = map.getView();
  const currentZoom = view.getZoom();

  if (currentZoom === undefined) {
    view.setCenter(coordinate);
    view.setZoom(targetZoom);
    return;
  }

  const mapSize = map.getSize();
  const isInView =
    mapSize && containsCoordinate(view.calculateExtent(mapSize), coordinate);

  // Never zoom out: use the higher of current zoom and target zoom
  const newZoom = Math.max(currentZoom, targetZoom);

  if (isInView && currentZoom >= targetZoom) {
    // Target already visible at a good zoom — just pan
    if (animate) {
      view.animate({ center: coordinate, duration });
    } else {
      view.setCenter(coordinate);
    }
  } else {
    // Target not visible or we're zoomed too far out — pan + zoom in
    if (animate) {
      view.animate({ center: coordinate, zoom: newZoom, duration });
    } else {
      view.setCenter(coordinate);
      view.setZoom(newZoom);
    }
  }
};
